import React from 'react'
import {css} from '@emotion/react'
import styled from '@emotion/styled'

import {
  getBookWithHotelIcon,
  getProviderIcon,
  getProviderName,
  getSvgProviderLogo,
  isBookWithHotelProvider
} from '@daedalus/core/src/utils/provider'

import {BookWithHotel} from './BookWithHotel'
import {ICON_HEIGHT, LOGO_HEIGHT} from './constants'

export type ProviderLogoVariant = 'logo' | 'icon'

interface Props {
  /** Pass through classname to allow styles overrides */
  className?: string
  /** Which provider's logo to display */
  providerCode: string
  /** The URL that contains the provider icons assets */
  urlPrefix: string
  /** The variant of the logo to display */
  variant?: ProviderLogoVariant
  /** The height of the image */
  height?: number
  loading?: 'eager' | 'lazy'
}

const Wrapper = styled.div<{variant: ProviderLogoVariant; height?: number}>(
  ({theme, variant, height}) => css`
    display: flex;
    align-items: center;

    img {
      display: block;
      margin-right: ${theme.layout.spacing.s200}px;
      height: ${variant === 'logo' ? LOGO_HEIGHT : ICON_HEIGHT}px;
      ${!!height && `height: ${height}px;`}

      &:only-child {
        margin-right: 0;
      }
    }
  `
)

export const ProviderLogo = ({
  urlPrefix,
  providerCode,
  variant = 'logo',
  height,
  className,
  loading = 'lazy'
}: Props) => {
  const isBookWithHotel = isBookWithHotelProvider(providerCode)
  const providerName = getProviderName(providerCode)
  const imageTitle = isBookWithHotel ? 'Book with hotel' : providerName

  let url =
    variant === 'logo'
      ? getSvgProviderLogo(providerCode, urlPrefix)
      : getProviderIcon({providerCode, iconsUrlPrefix: urlPrefix})

  if (isBookWithHotel) {
    url = getBookWithHotelIcon(urlPrefix)
  }

  if (variant === 'logo' && isBookWithHotel) return <BookWithHotel />

  return (
    <Wrapper variant={variant} height={height} className={className}>
      <img
        src={url}
        alt={`${providerCode}-logo`}
        title={imageTitle}
        height={height}
        loading={loading}
      />
    </Wrapper>
  )
}
